import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  LayoutDashboard,
  Users,
  TrendingUp,
  DollarSign,
  Download,
  Filter,
  RefreshCw,
  BarChart3,
  Calculator,
  Eye,
  ChevronLeft,
  ChevronRight,
  Home,
  Settings,
  HelpCircle,
  Loader2,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BUSINESS_TYPES = [
  { value: "all", label: "All Types" },
  { value: "saas", label: "SaaS" },
  { value: "ecommerce", label: "Ecommerce" },
  { value: "content", label: "Content Website" },
  { value: "marketplace", label: "Marketplace" },
  { value: "app", label: "App" },
  { value: "agency", label: "Agency" },
];

const AdminDashboard = () => {
  const [analytics, setAnalytics] = useState(null);
  const [valuations, setValuations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [selectedValuation, setSelectedValuation] = useState(null);
  const [showDetail, setShowDetail] = useState(false);

  // Filters
  const [businessType, setBusinessType] = useState("all");
  const [minValuation, setMinValuation] = useState("");
  const [maxValuation, setMaxValuation] = useState("");

  // Pagination
  const [page, setPage] = useState(0);
  const [pageSize] = useState(10);

  useEffect(() => {
    fetchData();
  }, [businessType, minValuation, maxValuation, page]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [analyticsRes, valuationsRes] = await Promise.all([
        axios.get(`${API_URL}/analytics`),
        axios.get(`${API_URL}/valuations`, {
          params: {
            business_type: businessType !== "all" ? businessType : undefined,
            min_valuation: minValuation || undefined,
            max_valuation: maxValuation || undefined,
            skip: page * pageSize,
            limit: pageSize,
          },
        }),
      ]);
      setAnalytics(analyticsRes.data);
      setValuations(valuationsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API_URL}/valuations/export/csv`, {
        params: {
          business_type: businessType !== "all" ? businessType : undefined,
          min_valuation: minValuation || undefined,
          max_valuation: maxValuation || undefined,
        },
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "valuations_export.csv");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("CSV exported successfully");
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export CSV");
    } finally {
      setExporting(false);
    }
  };

  const formatCurrency = (value) => {
    if (!value && value !== 0) return "-";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getBusinessTypeBadge = (type) => {
    const colors = {
      saas: "bg-blue-100 text-blue-700",
      ecommerce: "bg-purple-100 text-purple-700",
      content: "bg-green-100 text-green-700",
      marketplace: "bg-orange-100 text-orange-700",
      app: "bg-pink-100 text-pink-700",
      agency: "bg-slate-100 text-slate-700",
    };
    return colors[type] || "bg-slate-100 text-slate-700";
  };

  const StatCard = ({ title, value, icon: Icon, subtitle, trend }) => (
    <Card className="stat-card transition-all duration-300 border-slate-200">
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm font-medium text-slate-500 mb-1">{title}</p>
            <p className="text-2xl font-bold font-heading text-slate-900">{value}</p>
            {subtitle && <p className="text-xs text-slate-400 mt-1">{subtitle}</p>}
          </div>
          <div className="p-3 bg-emerald-100 rounded-xl">
            <Icon className="w-5 h-5 text-emerald-600" />
          </div>
        </div>
        {trend !== undefined && (
          <div className="mt-3 flex items-center gap-1">
            <TrendingUp className="w-4 h-4 text-emerald-500" />
            <span className="text-sm text-emerald-600 font-medium">
              {trend}% conversion rate
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 h-full w-64 bg-white border-r border-slate-200 z-50 hidden lg:block">
        <div className="p-6">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 bg-emerald-600 rounded-xl flex items-center justify-center">
              <Calculator className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-heading font-bold text-slate-900">Valuation</h1>
              <p className="text-xs text-slate-500">Admin Dashboard</p>
            </div>
          </div>

          <nav className="space-y-1">
            <a
              href="/admin"
              className="flex items-center gap-3 px-4 py-3 rounded-xl bg-emerald-50 text-emerald-700 font-medium"
            >
              <LayoutDashboard className="w-5 h-5" />
              Dashboard
            </a>
            <a
              href="/"
              className="flex items-center gap-3 px-4 py-3 rounded-xl text-slate-600 hover:bg-slate-50 transition-colors"
            >
              <Home className="w-5 h-5" />
              View Widget
            </a>
            <a
              href="/embed"
              className="flex items-center gap-3 px-4 py-3 rounded-xl text-slate-600 hover:bg-slate-50 transition-colors"
            >
              <Settings className="w-5 h-5" />
              Embed Code
            </a>
          </nav>
        </div>

        <div className="absolute bottom-0 left-0 right-0 p-6 border-t border-slate-100">
          <div className="flex items-center gap-3 text-slate-500 text-sm">
            <HelpCircle className="w-4 h-4" />
            <span>Need help? Contact support</span>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 p-6 md:p-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
          <div>
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-slate-900 mb-1">
              Dashboard
            </h1>
            <p className="text-slate-500">Monitor your valuation tool performance</p>
          </div>
          <div className="flex gap-3 mt-4 md:mt-0">
            <Button
              variant="outline"
              onClick={fetchData}
              disabled={loading}
              className="h-10"
              data-testid="btn-refresh"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
            <Button
              onClick={handleExportCSV}
              disabled={exporting}
              className="h-10 bg-emerald-600 hover:bg-emerald-700"
              data-testid="btn-export-csv"
            >
              {exporting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              Export CSV
            </Button>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatCard
            title="Widget Loads"
            value={analytics?.widget_loads?.toLocaleString() || "0"}
            icon={Eye}
            subtitle="Total impressions"
          />
          <StatCard
            title="Completed Valuations"
            value={analytics?.completed_valuations?.toLocaleString() || "0"}
            icon={Calculator}
            subtitle="Total submissions"
          />
          <StatCard
            title="Conversion Rate"
            value={`${analytics?.conversion_rate || 0}%`}
            icon={TrendingUp}
            subtitle="Load to completion"
          />
          <StatCard
            title="Average Valuation"
            value={formatCurrency(analytics?.average_valuation || 0)}
            icon={DollarSign}
            subtitle="Across all submissions"
          />
        </div>

        {/* Business Type Distribution */}
        {analytics?.valuations_by_type && Object.keys(analytics.valuations_by_type).length > 0 && (
          <Card className="mb-8 border-slate-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-emerald-600" />
                Valuations by Business Type
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-4">
                {Object.entries(analytics.valuations_by_type).map(([type, count]) => (
                  <div
                    key={type}
                    className="flex items-center gap-3 bg-slate-50 rounded-lg px-4 py-3"
                  >
                    <Badge className={getBusinessTypeBadge(type)}>
                      {type.charAt(0).toUpperCase() + type.slice(1)}
                    </Badge>
                    <span className="font-semibold text-slate-900">{count}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Filters */}
        <Card className="mb-6 border-slate-200">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Filter className="w-5 h-5 text-emerald-600" />
              Filters
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-600 text-sm">Business Type</Label>
                <Select value={businessType} onValueChange={setBusinessType}>
                  <SelectTrigger data-testid="filter-business-type">
                    <SelectValue placeholder="All Types" />
                  </SelectTrigger>
                  <SelectContent>
                    {BUSINESS_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-600 text-sm">Min Valuation</Label>
                <Input
                  type="number"
                  placeholder="e.g., 100000"
                  value={minValuation}
                  onChange={(e) => setMinValuation(e.target.value)}
                  data-testid="filter-min-valuation"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-600 text-sm">Max Valuation</Label>
                <Input
                  type="number"
                  placeholder="e.g., 1000000"
                  value={maxValuation}
                  onChange={(e) => setMaxValuation(e.target.value)}
                  data-testid="filter-max-valuation"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Valuations Table */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-emerald-600" />
              Recent Valuations
            </CardTitle>
            <CardDescription>Click on a row to view details</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
              </div>
            ) : valuations.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <Calculator className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                <p>No valuations found</p>
                <p className="text-sm">Valuations will appear here once submitted</p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-slate-100">
                        <TableHead className="text-slate-600">Lead</TableHead>
                        <TableHead className="text-slate-600">Business Type</TableHead>
                        <TableHead className="text-slate-600">Valuation</TableHead>
                        <TableHead className="text-slate-600">Multiple</TableHead>
                        <TableHead className="text-slate-600">Date</TableHead>
                        <TableHead className="text-slate-600 w-[50px]"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {valuations.map((v) => (
                        <TableRow
                          key={v.id}
                          className="table-row cursor-pointer border-slate-100 hover:bg-slate-50"
                          onClick={() => {
                            setSelectedValuation(v);
                            setShowDetail(true);
                          }}
                          data-testid={`valuation-row-${v.id}`}
                        >
                          <TableCell>
                            <div>
                              <p className="font-medium text-slate-900">{v.lead?.name}</p>
                              <p className="text-sm text-slate-500">{v.lead?.email}</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge className={getBusinessTypeBadge(v.business?.business_type)}>
                              {v.business?.business_type?.charAt(0).toUpperCase() +
                                v.business?.business_type?.slice(1) || "-"}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div>
                              <p className="font-semibold text-slate-900">
                                {formatCurrency(v.valuation_average)}
                              </p>
                              <p className="text-xs text-slate-500">
                                {formatCurrency(v.valuation_low)} - {formatCurrency(v.valuation_high)}
                              </p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <span className="text-emerald-600 font-medium">
                              {v.adjusted_multiple}×
                            </span>
                          </TableCell>
                          <TableCell className="text-slate-500 text-sm">
                            {formatDate(v.created_at)}
                          </TableCell>
                          <TableCell>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                              <Eye className="w-4 h-4 text-slate-400" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                {/* Pagination */}
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-100">
                  <p className="text-sm text-slate-500">
                    Showing {page * pageSize + 1} - {page * pageSize + valuations.length} results
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => Math.max(0, p - 1))}
                      disabled={page === 0}
                      data-testid="btn-prev-page"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => p + 1)}
                      disabled={valuations.length < pageSize}
                      data-testid="btn-next-page"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Detail Dialog */}
      <Dialog open={showDetail} onOpenChange={setShowDetail}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calculator className="w-5 h-5 text-emerald-600" />
              Valuation Details
            </DialogTitle>
            <DialogDescription>
              Submitted on {formatDate(selectedValuation?.created_at)}
            </DialogDescription>
          </DialogHeader>

          {selectedValuation && (
            <div className="space-y-6">
              {/* Lead Info */}
              <div>
                <h3 className="font-semibold text-slate-900 mb-3">Lead Information</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-slate-500">Name</p>
                    <p className="font-medium">{selectedValuation.lead?.name}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Email</p>
                    <p className="font-medium">{selectedValuation.lead?.email}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Phone</p>
                    <p className="font-medium">{selectedValuation.lead?.phone}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Website</p>
                    <p className="font-medium truncate">{selectedValuation.lead?.website_url}</p>
                  </div>
                </div>
              </div>

              {/* Business Info */}
              <div>
                <h3 className="font-semibold text-slate-900 mb-3">Business Details</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-slate-500">Type</p>
                    <Badge className={getBusinessTypeBadge(selectedValuation.business?.business_type)}>
                      {selectedValuation.business?.business_type}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-slate-500">Years Operating</p>
                    <p className="font-medium">{selectedValuation.business?.years_operating} years</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Monthly Revenue</p>
                    <p className="font-medium">
                      {formatCurrency(selectedValuation.business?.monthly_revenue)}
                    </p>
                  </div>
                  <div>
                    <p className="text-slate-500">Monthly Profit</p>
                    <p className="font-medium">
                      {formatCurrency(selectedValuation.business?.monthly_profit)}
                    </p>
                  </div>
                  <div>
                    <p className="text-slate-500">Growth Rate</p>
                    <p className="font-medium">{selectedValuation.business?.revenue_growth_rate}%</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Owner Hours/Week</p>
                    <p className="font-medium">{selectedValuation.business?.owner_hours_per_week}h</p>
                  </div>
                </div>
              </div>

              {/* Valuation Results */}
              <div className="bg-emerald-50 rounded-xl p-4">
                <h3 className="font-semibold text-emerald-800 mb-3">Valuation Results</h3>
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-emerald-600 text-sm">Low</p>
                    <p className="text-xl font-bold text-emerald-800">
                      {formatCurrency(selectedValuation.valuation_low)}
                    </p>
                  </div>
                  <div className="bg-emerald-100 rounded-lg py-2">
                    <p className="text-emerald-600 text-sm">Expected</p>
                    <p className="text-2xl font-bold text-emerald-800">
                      {formatCurrency(selectedValuation.valuation_average)}
                    </p>
                  </div>
                  <div>
                    <p className="text-emerald-600 text-sm">High</p>
                    <p className="text-xl font-bold text-emerald-800">
                      {formatCurrency(selectedValuation.valuation_high)}
                    </p>
                  </div>
                </div>
                <p className="text-center text-sm text-emerald-700 mt-3">
                  Multiple Applied: <strong>{selectedValuation.adjusted_multiple}×</strong> monthly profit
                </p>
              </div>

              {/* AI Commentary */}
              {selectedValuation.ai_commentary && (
                <div>
                  <h3 className="font-semibold text-slate-900 mb-3">AI Commentary</h3>
                  <div className="bg-slate-50 rounded-xl p-4 text-sm text-slate-600 whitespace-pre-wrap">
                    {selectedValuation.ai_commentary}
                  </div>
                </div>
              )}

              {/* Exit Recommendation */}
              {selectedValuation.exit_recommendation && (
                <div className="bg-blue-50 rounded-xl p-4">
                  <h3 className="font-semibold text-blue-800 mb-2">Exit Recommendation</h3>
                  <p className="text-blue-700">{selectedValuation.exit_recommendation}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
